// ogsFileIcon — which glyph an /ogs bundle file gets on its card, and how
// strongly it is drawn. Pure and leaf (it reads ogsContentSelect, which is
// itself the pure half), so the classification is testable under `node --test`
// away from any React.
//
// It exists because /ogs is ONE asset kind holding a dozen different jobs: a
// real bundle is ~30 rows and every card drew the same fallback crystal, so the
// filename was the only thing telling a decoder apart from a manifest. The
// category IS the file's job in the pipeline — the same question ogsFileRole
// answers for the inspector, asked one level finer.
//
// Strength is the OTHER half of that question and comes from ogsFileRole
// unchanged: a required file is drawn at full strength, an optional or extra
// one dimmed. So a folder shows the bundle's shape at a glance — what it needs
// against what it merely carries — which is exactly the question someone
// opening /ogs is usually there to ask.

import { ogsFileRole } from './ogsContentSelect.js'

/**
 * The glyph family for one bundle filename:
 *
 *   decoder  — geom / appr / trunk: the ONNX stages that decode the splats.
 *   rig      — what poses the character: orl_pack (the DNA-derived rig),
 *              rig_names / rig_params / rig_presets, and rig2mesh (the
 *              distilled rig→verts model — an .onnx, but the RIG is what it is,
 *              and "which of these is the rig" is the question being asked).
 *   mesh     — the geometry the lift anchors to: mesh_*, frame_vertices,
 *              vertex_association, vis_mask, pose_anchors.
 *   manifest — what the bundle SAYS about itself: scene.json,
 *              export_manifest.json, deploy_info.
 *   driver   — a .glb: the part-drive skeleton and its clips.
 *   splat    — pre-baked gaussians (hair.gsh).
 *   file     — anything else. A bundle may carry files this app never reads,
 *              and a wrong specific icon would be worse than a neutral one.
 */
export function ogsIconCategory(filename) {
  const name = typeof filename === 'string' ? filename.trim().toLowerCase() : ''
  if (!name) return 'file'

  // Extension first: a .glb in a bundle is a driver asset whatever it is named
  // (the same rule ogsFileRole applies), and .gsh is baked gaussians.
  if (name.endsWith('.glb')) return 'driver'
  if (name.endsWith('.gsh') || name.endsWith('.ply') || name.endsWith('.spz')) return 'splat'

  const base = stemOf(name)
  // Longest prefix wins, so `rig2mesh` is not shadowed by `rig` and
  // `rig_names` is not read as a bare `rig`.
  for (const [prefix, category] of PREFIXES) {
    if (base === prefix || base.startsWith(`${prefix}_`)) return category
  }
  return 'file'
}

/**
 * How strongly the glyph is drawn: 'full' for a file the render bundle
 * REQUIRES, 'dim' for one it can do without. Straight off ogsFileRole, so the
 * card and the inspector's Role field can never disagree.
 */
export function ogsIconStrength(filename) {
  return ogsFileRole(filename) === 'required' ? 'full' : 'dim'
}

// Ordered longest-first at module level rather than sorted per call — this runs
// once per card in a 30-card grid.
const PREFIXES = [
  ['export_manifest', 'manifest'],
  ['vertex_association', 'mesh'],
  ['frame_vertices', 'mesh'],
  ['pose_anchors', 'mesh'],
  ['deploy_info', 'manifest'],
  ['rig_presets', 'rig'],
  ['rig_params', 'rig'],
  ['rig_names', 'rig'],
  ['rig2mesh', 'rig'],
  ['orl_pack', 'rig'],
  ['vis_mask', 'mesh'],
  ['manifest', 'manifest'],
  ['trunk', 'decoder'],
  ['scene', 'manifest'],
  ['appr', 'decoder'],
  ['geom', 'decoder'],
  ['mesh', 'mesh'],
].sort((a, b) => b[0].length - a[0].length)

function stemOf(name) {
  const dot = name.lastIndexOf('.')
  const base = dot > 0 ? name.slice(0, dot) : name
  return base.endsWith('_fp16') ? base.slice(0, -'_fp16'.length) : base
}
